import { useEffect } from "react";

interface SEOHeadProps {
  title: string;
  description: string;
  path: string;
  keywords?: string;
  type?: string;
  jsonLd?: object;
  image?: string;
  noindex?: boolean;
  publishedTime?: string;
  modifiedTime?: string;
  section?: string;
  tags?: string[];
}

const DEFAULT_IMAGE =
  "https://storage.googleapis.com/gpt-engineer-file-uploads/1h8DGeFvswWsXDJIbhp9Uzo2KiN2/social-images/social-1772665804337-ChatGPT_Image_Dec_8,_2025,_04_41_27_AM.webp";

const SEOHead = ({
  title,
  description,
  path,
  keywords,
  type = "website",
  jsonLd,
  image,
  noindex,
  publishedTime,
  modifiedTime,
  section,
  tags,
}: SEOHeadProps) => {
  const baseUrl = "https://synra-ai.com";
  const fullUrl = `${baseUrl}${path}`;
  const ogImage = image || DEFAULT_IMAGE;

  useEffect(() => {
    document.title = title;

    const setMeta = (name: string, content: string, attr = "name") => {
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    setMeta("description", description);
    if (keywords) setMeta("keywords", keywords);
    setMeta(
      "robots",
      noindex
        ? "noindex, nofollow"
        : "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"
    );
    setMeta("og:title", title, "property");
    setMeta("og:description", description, "property");
    setMeta("og:url", fullUrl, "property");
    setMeta("og:type", type, "property");
    setMeta("og:site_name", "SYNRA", "property");
    setMeta("og:image", ogImage, "property");
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);
    setMeta("twitter:image", ogImage);
    if (publishedTime) setMeta("article:published_time", publishedTime, "property");
    if (modifiedTime) setMeta("article:modified_time", modifiedTime, "property");
    if (section) setMeta("article:section", section, "property");
    (tags || []).slice(0, 8).forEach((t) => {
      const el = document.createElement("meta");
      el.setAttribute("property", "article:tag");
      el.setAttribute("content", t);
      el.setAttribute("data-page-meta", "1");
      document.head.appendChild(el);
    });

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", fullUrl);

    // Inject page-specific JSON-LD
    let jsonLdScript: HTMLScriptElement | null = null;
    if (jsonLd) {
      jsonLdScript = document.createElement("script");
      jsonLdScript.type = "application/ld+json";
      jsonLdScript.id = "page-jsonld";
      jsonLdScript.textContent = JSON.stringify(jsonLd);
      // Remove existing page-level JSON-LD
      document.querySelector("#page-jsonld")?.remove();
      document.head.appendChild(jsonLdScript);
    }

    return () => {
      document.title = "SYNRA — منصة الذكاء الاصطناعي والأتمتة في المغرب";
      document.querySelector("#page-jsonld")?.remove();
      document.querySelectorAll('meta[data-page-meta="1"]').forEach((n) => n.remove());
    };
  }, [title, description, fullUrl, type, keywords, jsonLd, ogImage, noindex, publishedTime, modifiedTime, section, tags]);

  return null;
};

export default SEOHead;
